import { EntityForm } from '@/common/entityFramework/entityForm/EntityForm';
import { useFieldDefs } from '@/common/entityFramework/entityForm/FieldDefsContext';
import { useEntityMgr } from '@/common/entityFramework/entityView/EntityMgrContext';
import { DynamicFormField } from '@/common/form/formFields/DynamicFormField';
import { FormFields } from '@/common/form/rendering/FormFields';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { CalcTypeValue } from '@/md/entities/progProd/formFields/value/CalcTypeValue';
import { useEffect, useState } from 'react';
import { TierCode } from './formFields/TierCode';
import { TierFormulaField } from './formFields/TierFormulaField';
import { validateFormula } from './formFields/TierFormulaUtils';

// A single, unnamed section isn't worth a "General" card title - just render the fields directly.
// Status/Audit fields are normally split off onto their own cards elsewhere in the layout, not listed here.
const TierFormFields = () => {
  const { fieldDefs } = useFieldDefs();
  const visibleFieldDefs = fieldDefs?.filter(
    (fieldDef) => fieldDef.section !== 'Status' && fieldDef.section !== 'Audit',
  );
  return <FormFields fieldDefs={visibleFieldDefs} />;
};

// ownerObj (the owning PROG_PROD) may be supplied by an embedding parent; otherwise it is resolved
// from the route-level Tier being viewed
export const ProgProdTierForm = ({ ownerObj, ...formProps }) => {
  const { entityObj } = useEntityMgr();
  const rsrc = useRsrc();
  const [tierObj, setTierObj] = useState(ownerObj ?? null);

  useEffect(() => {
    if (ownerObj) {
      setTierObj(ownerObj);
      return;
    }
    const progProdId = entityObj?.progProdId;
    if (!progProdId) return;

    rsrc.byId('md', 'PROG_PROD', progProdId).then((res) => {
      setTierObj(res);
    });
  }, [ownerObj, entityObj, rsrc]);

  if (!tierObj) return <></>;

  const augFieldDefs = (fieldToFieldDef) => {
    fieldToFieldDef.code.component = <TierCode tierObj={tierObj} />;
    // The Tier number determines ordering/the code suffix - not something a user should be able to change after the
    // fact; editLevel (rather than disabled directly) is used since EntityForm recomputes .disabled from it on load
    fieldToFieldDef.tier.editLevel = 'NEVER';
    fieldToFieldDef.conc.component = <CalcTypeValue fieldDef={fieldToFieldDef.conc} fallback={DynamicFormField} />;
    const pymtBasisIdToCode = Object.fromEntries(
      fieldToFieldDef.pymtBasisId.values.map((enumSpec) => [enumSpec.id, enumSpec.code]),
    );
    fieldToFieldDef.formula.component = (
      <TierFormulaField pymtBasisIdToCode={pymtBasisIdToCode} fieldDef={fieldToFieldDef.formula} />
    );
    // Last and full-width - formulas run long
    fieldToFieldDef.formula.precedence = 10000;
    fieldToFieldDef.formula.validators = [(value) => validateFormula(value)];

    // Program Product is implied by which card/dialog the Tier is opened from - not useful to show or edit here
    fieldToFieldDef.progProdId.hide = true;
    // Threshold Formula is viewed/edited within the formula field's dialog
    fieldToFieldDef.thresholdFormula.hide = true;
  };

  return (
    <EntityForm entity="PROG_PROD_TIER" augFieldDefs={augFieldDefs} {...formProps}>
      <TierFormFields />
    </EntityForm>
  );
};
